/**
 * 화상통화 미디어 순수부 — 카메라·마이크 확보 순서, 안내 문구, 상대에게 보내는 상태 메시지.
 *
 * ⚠ import 금지: scripts/test-call-media.js가 typescript.transpileModule로 통째로 평가해 node에서 검증한다.
 * getUserMedia·permissions.query는 인자로 받는다(가짜 구현을 넣어 순서를 테스트한다). 모듈 로드 시 DOM을 만지지 않는다.
 *
 * 정책(2026-11-03): 카메라·마이크가 없거나 쓸 수 없어도 통화는 연결된다. 있는 것만 켜고, 둘 다 없으면 보기 전용.
 * 권한을 거부한 경우도 보기 전용으로 연결한다(사용자 결정).
 */

/** 장치를 쓸 수 없는 이유 */
export type MediaCause = 'not-found' | 'busy' | 'denied' | 'insecure' | 'error';

export interface TrackLike {
  kind: string;
}

export interface StreamLike<T extends TrackLike = TrackLike> {
  getTracks(): T[];
}

export type GetUserMediaFn<T extends TrackLike = TrackLike> = (
  constraints: { video?: boolean; audio?: boolean },
) => Promise<StreamLike<T>>;

/** 'granted' | 'denied' | 'prompt' — 조회할 수 없으면 reject 또는 그 밖의 값 */
export type QueryPermissionFn = (name: 'camera' | 'microphone') => Promise<string>;

export interface AcquiredMedia<T extends TrackLike = TrackLike> {
  /** 실제 장치 트랙(대체 트랙은 호출자가 붙인다) */
  tracks: T[];
  hasMic: boolean;
  hasCamera: boolean;
  /** hasMic이 false일 때만 의미가 있다 */
  micCause: MediaCause | null;
  /** hasCamera가 false일 때만 의미가 있다 */
  cameraCause: MediaCause | null;
}

export function classifyMediaError(err: unknown): MediaCause {
  const name = err && typeof err === 'object' ? String((err as { name?: unknown }).name || '') : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'not-found';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'busy';
    default:
      return 'error';
  }
}

function build<T extends TrackLike>(
  tracks: T[],
  micCause: MediaCause | null,
  cameraCause: MediaCause | null,
): AcquiredMedia<T> {
  const hasMic = tracks.some(t => t.kind === 'audio');
  const hasCamera = tracks.some(t => t.kind === 'video');
  return {
    tracks,
    hasMic,
    hasCamera,
    micCause: hasMic ? null : (micCause ?? 'error'),
    cameraCause: hasCamera ? null : (cameraCause ?? 'error'),
  };
}

async function permissionState(query: QueryPermissionFn | null | undefined, name: 'camera' | 'microphone'): Promise<string> {
  if (!query) return 'unknown';
  try {
    return await query(name);
  } catch {
    return 'unknown';
  }
}

/**
 * 카메라·마이크를 가능한 만큼 확보한다. 예외를 던지지 않는다.
 *
 * 1. getUserMedia가 없으면(HTTP·미지원) 보기 전용
 * 2. 카메라+마이크를 함께 요청
 * 3. 권한 거부면 permissions.query가 'granted'인 종류만 따로 다시 요청한다.
 *    'prompt'인 종류는 다시 요청하지 않는다 — 권한 창이 연달아 뜬다.
 * 4. 그 밖의 실패(장치 없음·사용 중)는 마이크만 → 카메라만 순서로 다시 요청한다.
 *    마이크만 요청하다 권한을 거부당하면 카메라는 묻지 않는다(창을 또 띄우지 않는다).
 */
export async function acquireCallMedia<T extends TrackLike>(
  getUserMedia: GetUserMediaFn<T> | null | undefined,
  queryPermission?: QueryPermissionFn | null,
): Promise<AcquiredMedia<T>> {
  if (!getUserMedia) return build<T>([], 'insecure', 'insecure');

  let firstCause: MediaCause;
  try {
    const stream = await getUserMedia({ video: true, audio: true });
    return build(stream.getTracks(), null, null);
  } catch (err) {
    firstCause = classifyMediaError(err);
  }

  if (firstCause === 'denied') {
    const [micState, camState] = await Promise.all([
      permissionState(queryPermission, 'microphone'),
      permissionState(queryPermission, 'camera'),
    ]);
    const tracks: T[] = [];
    let micCause: MediaCause = 'denied';
    let cameraCause: MediaCause = 'denied';
    if (micState === 'granted') {
      try {
        tracks.push(...(await getUserMedia({ audio: true })).getTracks());
      } catch (err) {
        micCause = classifyMediaError(err);
      }
    }
    if (camState === 'granted') {
      try {
        tracks.push(...(await getUserMedia({ video: true })).getTracks());
      } catch (err) {
        cameraCause = classifyMediaError(err);
      }
    }
    return build(tracks, micCause, cameraCause);
  }

  // 장치 없음·사용 중·그 밖의 오류 — 어느 쪽이 원인인지 하나씩 확인한다
  let micCause: MediaCause;
  try {
    const audio = await getUserMedia({ audio: true });
    // 마이크만은 된다 → 카메라가 원인
    return build(audio.getTracks(), null, firstCause);
  } catch (err) {
    micCause = classifyMediaError(err);
  }
  if (micCause === 'denied') return build<T>([], 'denied', firstCause);

  try {
    const video = await getUserMedia({ video: true });
    return build(video.getTracks(), micCause, null);
  } catch (err) {
    return build<T>([], micCause, classifyMediaError(err));
  }
}

// ─── 안내 문구 ─────────────────────────────────────────────────────────────

function permissionHint(isDesktopApp: boolean): string {
  return isDesktopApp
    ? 'Windows 설정 > 개인 정보 및 보안에서 카메라·마이크 접근을 허용하면 다음 통화부터 켜집니다.'
    : '주소창 옆 자물쇠 아이콘(사이트 설정)에서 허용하면 다음 통화부터 켜집니다.';
}

function oneDeviceNotice(device: '카메라' | '마이크', cause: MediaCause, isDesktopApp: boolean): string {
  const tail = device === '카메라'
    ? '상대에게는 내 이름이 보입니다.'
    : '내 목소리는 상대에게 전달되지 않습니다.';
  const obj = device === '카메라' ? '카메라를' : '마이크를';
  switch (cause) {
    case 'busy':
      return `${obj} 다른 프로그램이 쓰고 있어 ${device} 없이 연결합니다. ${tail}`;
    case 'denied':
      return `${device} 권한이 없어 ${device} 없이 연결합니다. ${permissionHint(isDesktopApp)}`;
    case 'error':
      return `${obj} 켜지 못해 ${device} 없이 연결합니다. ${tail}`;
    default:
      return `${device} 없이 연결합니다. ${tail}`;
  }
}

/**
 * 통화 화면에 들어가기 전에 띄울 안내 문구. 둘 다 켜졌으면 null.
 * 실패가 아니라 '이렇게 연결한다'는 안내라 info 톤이다.
 */
export function describeMediaNotice(media: AcquiredMedia, isDesktopApp: boolean): string | null {
  if (media.hasMic && media.hasCamera) return null;
  if (media.hasMic) return oneDeviceNotice('카메라', media.cameraCause ?? 'error', isDesktopApp);
  if (media.hasCamera) return oneDeviceNotice('마이크', media.micCause ?? 'error', isDesktopApp);

  const viewOnly = '상대 화면과 소리는 그대로 보고 들을 수 있습니다.';
  const causes = [media.micCause, media.cameraCause];
  if (causes.includes('insecure')) {
    return `이 주소에서는 카메라·마이크를 쓸 수 없어 보기 전용으로 연결합니다. ${viewOnly}`;
  }
  if (causes.includes('denied')) {
    return `카메라·마이크 권한이 없어 보기 전용으로 연결합니다. ${permissionHint(isDesktopApp)}`;
  }
  if (causes.every(c => c === 'not-found')) {
    return `카메라·마이크가 없어 보기 전용으로 연결합니다. ${viewOnly}`;
  }
  if (causes.every(c => c === 'busy')) {
    return `카메라·마이크를 다른 프로그램이 쓰고 있어 보기 전용으로 연결합니다. ${viewOnly}`;
  }
  return `카메라·마이크를 쓸 수 없어 보기 전용으로 연결합니다. ${viewOnly}`;
}

// ─── 상대에게 보내는 내 카메라·마이크 상태 (simple-peer 데이터 채널) ─────────

/**
 * audio = 내 목소리가 나가는 중, video = 내 영상(카메라 또는 화면 공유)이 나가는 중,
 * screen = 지금 나가는 영상이 화면 공유다(받는 쪽이 잘리지 않게 contain으로 그리고 크게 보여 준다 — 구버전은 보내지 않아 false)
 */
export interface CallMediaState {
  audio: boolean;
  video: boolean;
  screen: boolean;
}

export interface LocalMediaFlags {
  hasMic: boolean;
  hasCamera: boolean;
  muted: boolean;
  cameraOff: boolean;
  screenSharing: boolean;
}

export function computeMediaState(f: LocalMediaFlags): CallMediaState {
  return {
    audio: f.hasMic && !f.muted,
    video: (f.hasCamera && !f.cameraOff) || f.screenSharing,
    screen: f.screenSharing,
  };
}

const MEDIA_STATE_MAX_BYTES = 256;

export function encodeMediaState(s: CallMediaState): string {
  // v는 1 그대로 — 구버전 해석기는 s를 모르고 무시한다(a·c만 본다)
  return JSON.stringify({ t: 'media', v: 1, a: !!s.audio, c: !!s.video, s: !!s.screen });
}

/**
 * 데이터 채널로 받은 값을 읽는다. simple-peer는 Buffer(Uint8Array)로 준다.
 * 모르는 형식·너무 긴 값은 null — 나중에 다른 종류의 메시지가 생겨도 여기서 걸러진다.
 */
export function decodeMediaState(raw: unknown): CallMediaState | null {
  let text: string;
  if (typeof raw === 'string') {
    text = raw;
  } else if (raw instanceof Uint8Array || raw instanceof ArrayBuffer) {
    if (raw.byteLength > MEDIA_STATE_MAX_BYTES) return null;
    try {
      text = new TextDecoder().decode(raw);
    } catch {
      return null;
    }
  } else {
    return null;
  }
  if (text.length > MEDIA_STATE_MAX_BYTES) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(text);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as { t?: unknown; a?: unknown; c?: unknown; s?: unknown };
  if (o.t !== 'media' || typeof o.a !== 'boolean' || typeof o.c !== 'boolean') return null;
  return { audio: o.a, video: o.c, screen: o.s === true };
}
